import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { ToastGlobalService } from 'src/app/layout/toast-global/toast-global.service';
import { Actions } from 'src/app/models/actions';
import { FleetSchedule } from 'src/app/models/fleetSchedule';
import { PagedResponse } from 'src/app/models/pagedResponse';
import { Part } from 'src/app/models/part';
import { TableFilter } from 'src/app/models/tableFilter';
import { Tenant } from 'src/app/models/tenant';
import { VehicleSchedule } from 'src/app/models/vehicleSchedule';
import { TenantState } from 'src/app/store/state/tenant.state';
import { PartService } from '../part/service/part.service';
import { ScheduleService } from '../schedule/schedule.service';
import { VehicleService } from '../vehicle/service/vehicle.service';
import { TranslateService } from '@ngx-translate/core';
import { VehicleState } from '../../models/vehicleState';
import { LocalizeService } from 'src/app/services/localize.service';
import { PartVehicle } from 'src/app/models/partVehicle';
import { DynamicDialogService } from 'src/app/dynamic-dialog/dynamic-dialog.service';
import { AppConfigurationService, AppSettings } from 'src/app/shared/services/app-configuration.service';
import { distinctUntilChanged, filter, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { TenantStateService } from 'src/app/shared/services/tenant.state.service';
import { Subject, combineLatest, of } from 'rxjs';
import { updateTabIndexRoute, getTabIndex } from 'src/app/shared/handler/common-functions.util';
import { ReferenceDataService } from 'src/app/shared/services/reference-data.service';
import { ReferenceData } from 'src/app/models/referenceData';
import { SpaRoutingService } from 'src/app/shared/services/spa.routing.service';

@Component({
    selector: 'app-vehicle-schedule-detail',
    templateUrl: './vehicle-schedule-detail.component.html',
    styleUrls: ['./vehicle-schedule-detail.component.css'],
    standalone: false
})
export class VehicleScheduleDetailComponent implements OnInit, OnDestroy {
  @SelectSnapshot(TenantState.getCurrentTenant) getCurrentTenant: Tenant;
  @SelectSnapshot(TenantState.getLanguage) getLanguage: string;
  vehicleSchedule: VehicleSchedule;
  fleetSchedules: FleetSchedule[];
  vin: string;
  tenantId: string;
  selectedIndex = 0;
  avgMph = 0;
  calculatedAvg = 0;
  deviationFromMean = 0;
  selected = 'updateDate';
  completedDate: Date;
  Actions: typeof Actions = Actions;
  lamps: any;
  vehicleState: VehicleState;
  partsData: PagedResponse;
  lastFilter = new TableFilter();
  availLoading = true;
  loading: boolean = false;
  displayOdometer: any;
  displayOperationDuration: string;
  currentLanguage: string;

  length: number;
  innerWidth: number;
  direction = 'horizontal';
  assetUrl: string;
  sensorType: string;

  sensorTypes: ReferenceData[];

  private destroy$ = new Subject();

  constructor(
    private vehicleService: VehicleService,
    private route: ActivatedRoute,
    private router: Router,
    private toastGlobalService: ToastGlobalService,
    private scheduleService: ScheduleService,
    private partService: PartService,
    private translate: TranslateService,
    private dialogService: DynamicDialogService,
    private localizeService: LocalizeService,
    private readonly appConfigurationService: AppConfigurationService,
    private tenantStateService: TenantStateService,
    private referenceDataService: ReferenceDataService,
    private spaRoutingService: SpaRoutingService) { }

  @HostListener('window:resize', ['$event'])
  onResize(event): void {
    this.innerWidth = window.innerWidth;
    this.direction = this.innerWidth >= 400 ? 'horizontal' : 'vertical';
  }

  ngOnInit(): void {
    const setSensorTypes$ = this.referenceDataService.getSensorTypes().pipe(tap(st => this.sensorTypes = st));

    this.tenantId = this.getCurrentTenant.externalId;
    this.currentLanguage = this.getLanguage;
    this.appConfigurationService.settings$
          .pipe(take(1))
          .subscribe((settings: AppSettings) => {
            this.assetUrl = this.appConfigurationService.settings.contentDeliveryNetworkUrl;
        });

    const fleetSchedules$ = this.scheduleService.getAllFleetSchedules(JSON.stringify({}), JSON.stringify({}), 0, 100)
      .pipe(tap(sch => this.fleetSchedules = sch.documents));

    this.route.paramMap.pipe(
      map(params => params.get('id')),
      filter((id): id is string => !!id),
      distinctUntilChanged(),
      tap(() => this.loading = true),
      switchMap(id =>
        combineLatest([
          this.vehicleService.selectedSchedule && id === this.vehicleService.selectedSchedule.id ?
            of(this.vehicleService.selectedSchedule) : this.vehicleService.getVehicleScheduleById(id),
          fleetSchedules$,
          setSensorTypes$
        ]).pipe(finalize(() => this.loading = false))
      ),
      takeUntil(this.destroy$)
    ).subscribe(([schedule]) => {
      this.vehicleSchedule = schedule;
      this.setDisplayValues(schedule);
    });

    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(p =>
        this.selectedIndex = Number(p.tabIndex || 0));
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  navigateToVehicle(): void {
    this.spaRoutingService.navigateToVehicle(
      this.route.snapshot.params.vin,
      { queryParams: { tabIndex: '6' } }
    );
  }

  setDisplayValues(sch: VehicleSchedule): void {
    if (this.vehicleSchedule.lastMaintenance) {
      if (this.vehicleSchedule.lastMaintenance.state.isComplete === 'true') {
        this.selected = 'completeDate';
        this.completedDate = this.vehicleSchedule.lastMaintenance.date.completeDate;
      } else {
        this.completedDate = this.vehicleSchedule.lastMaintenance.date.updateDate;
      }

      this.vehicleSchedule.lastMaintenance.vehicleState.dashboard.indicators.forEach(lamp => {
        this.vehicleSchedule.lastMaintenance.vehicleState.dashboard[lamp.name] = lamp.enabled;
      });

      // TODO: Fix me elsewhere, please.
      const currentLanguage = this.getLanguage;
      this.displayOdometer = this.localizeService.getDisplayOdometer(
        this.currentLanguage,
        this.vehicleSchedule?.lastMaintenance?.vehicleState?.odometer);
      this.displayOperationDuration = this.localizeService.getHours(
        this.vehicleSchedule?.lastMaintenance?.vehicleState?.engine?.operationDuration);

    }
    this.sensorType = this.sensorTypes.find(st => st.id == this.vehicleSchedule.schedule.sensorTypeId)?.name ?? "";
  }

  removeParts(event: any): void {
    if (event.parts) {
      this.vehicleSchedule.schedule.parts = event.parts;
    }
    this.saveVehicle();
  }

  addPart(currentPart: Part): void {
    const payload = {
      part: currentPart,
      layout: null,
      showMassAdd: false
    };
    this.dialogService.openAddPartDialog(payload).afterClosed().subscribe(res => {
      if (res) {
        currentPart.location = res.location;
        currentPart.vehicles = new Array<PartVehicle>();
        const newPartVehicle = new PartVehicle();
        newPartVehicle.partQuantity = res.quantity;
        currentPart.vehicles.push(newPartVehicle);
        this.vehicleSchedule.schedule.parts.push(currentPart);
        this.saveVehicle();
      }
    });
  }

  updateData(passedFilter: TableFilter): void {
    if (this.vehicleSchedule) {
      this.availLoading = true;
      this.partService.getVehicleScheduleData(
        this.vehicleSchedule.id,
        passedFilter.filter,
        passedFilter.sort,
        passedFilter.offset,
        passedFilter.limit).subscribe(res => {
          this.partsData = res.partResponse;
          this.lastFilter = passedFilter;
          this.availLoading = false;
        });
    }
  }

  openCFR(section) {
    this.appConfigurationService.settings$.pipe(take(1)).subscribe(c => {
      //split the section to determine if the section has subsections
      const split = section.match(/[a-zA-Z]+|[0-9]+/g);
      let url = '';
      if(split.length > 2) {
        //if the section has more than 2 parts it is assumed the extra data is the subsection, these need to be added as an anchor in the url
        url = c.ecfrUrl.replace("{section}", `${split[0]}.${split[1]}#p-${split[0]}.${split[1]}`);
        for(let i = 2; i < split.length; i++) {
          //apply each subsection in parens
          url += `(${split[i]})`;
        }
      } else {
        //if we have 2 or less parts of the section assume the formatting is correct
        url = c.ecfrUrl.replace("{section}", section);
      }
      window.open(url, "_blank");
    })
  }

  private saveVehicle(): void {
    this.vehicleService.postVehicleSchedule(this.vehicleSchedule)
      .pipe(
        tap(() => {
          this.updateData(this.lastFilter);
          const toast = this.translate.instant('@updateSuccessful');
          this.toastGlobalService.show(toast, { classname: 'success' });
        }),
        switchMap(() => this.vehicleService.getVehicleScheduleById(
          this.route.snapshot.params.id))
      )
      .subscribe(res => {
        this.vehicleSchedule = res;
        this.setDisplayValues(res);
      });
    
  }

  onTabClick(event) {
    updateTabIndexRoute(event.index, this.router, this.route);
  }

}
